import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";

import Button from "@cayuse/shared-react.components.button";
import CovertButton from "@cayuse/shared-react.components.covert-button";
import FormattedDatetime from "@cayuse/shared-react.components.formatted-datetime";
import FormattedRichText from "@cayuse/shared-react.components.formatted-rich-text";
import Icon from "@cayuse/shared-react.components.icon";
import RichText from "@cayuse/shared-react.components.form.inputs.rich-text";
import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";
import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import PagingControls from "@cayuse/shared-react.components.paging-controls";

import { shellSelectors } from "store/selectors";
import { useDictionary } from "hooks";
import { useRichTextAccessibility } from "hooks/accessibility";
import { sortCollectionByKey, spUrl, findUsers, notifyError, decodeString, convertToPlainText } from "utils";
import { put, remove, save, request } from "utils/proxy";
import { IS_DEV, COLUMN_HEADER_KEYS, DEFAULT_PAGE_LIMIT, NOTES_MAX_LENGTH } from "globals/constants";

import Table from "components/Table";
import ConfirmationModal from "components/ConfirmationModal";
import NoteDisplayModal from "components/NoteDisplayModal";
import Pager from "components/Pager";

import styles from "./styles.css";
import { normalizeNoteMessage } from "./utils";

const richTextFormats = [
	"background",
	"bold",
	"color",
	"font",
	"code",
	"italic",
	"link",
	"size",
	"strike",
	"script",
	"underline",
	"blockquote",
	"header",
	"indent",
	"list",
	"align",
	"direction",
	"code-block",
	"formula"
];

const FormNotesView = ({
	formInstance,
	canAddNotes,
	canEditNotes,
}) => {
	const dictionary = useDictionary("common.notes");
	const [ notes, setNotes ] = useState([]);
	const [ updateNotes, setUpdateNotes ] = useState(false);
	const [ isLoadingNotes, setIsLoadingNotes ] = useState(true);
	const [ userNames, setUserNames ] = useState([]);
	const [ hasNotes, setHasNotes ] = useState(isNonEmptyArray(notes));
	const [ newNoteMessage, setNewNoteMessage ] = useState("");
	const [ isConfirmDeleteOpen, setIsConfirmDeleteOpen ] = useState(false);
	const [ sortOrder, setSortOrder ] = useState("descending");
	const [ updateNames, setUpdateNames ] = useState(false);
	const [ reloadNotes, setReloadNotes ] = useState(true);

	const [ itemsPerPage, setItemsPerPage ] = useState(DEFAULT_PAGE_LIMIT);
	const [ pageNumber, setPageNumber ] = useState(0);
	const [ numberOfPages, setNumberOfPages ] = useState(0);
	const [ totalNotes, setTotalNotes ] = useState(0);

	const [ noteModal, setNoteModal ] = useState({
		canEdit: false,
		noteIndex: 0,
		isVisible: false,
	});
	
	const { personId } = useSelector(shellSelectors.user);

	const richTextRef = useRef(null);

	useRichTextAccessibility(richTextRef);

	const isProject =  formInstance.type === COLUMN_HEADER_KEYS.PROJECT;
	const hasNewNoteMessage = isNonEmptyString(convertToPlainText(newNoteMessage));

	const onNoteOpen = (canEdit, noteIndex) => {
		setNoteModal({
			canEdit,
			noteIndex,
			isVisible: true,
		});
	};

	const onNoteClose = () => {
		setNoteModal({ ...noteModal, isVisible: false });
	};

	const onNewNoteChange = value => {
		setNewNoteMessage(value);
	};

	const handleOnSave = () => {
		if (!hasInvalidData()) {
			const url = spUrl(`${formInstance.endpoint}/${formInstance.instanceId}/notes`);

			const payload = {
				message: encodeURI(newNoteMessage),
			};

			save(url, payload)
				.then(() => setReloadNotes(true))
				.catch(error => {
					const message = IS_DEV ? error.message : dictionary.error;
					notifyError(message, error);
				});
			setNewNoteMessage("");
		}
	};

	const hasInvalidData = () => {
		return convertToPlainText(newNoteMessage).length > NOTES_MAX_LENGTH;
	};

	const handleOnUpdate = (index, newValue) => {
		const noteId = notes[index].noteId;
		const payload = {
			message: newValue.message,
		};
		put(spUrl(`${formInstance.endpoint}/${formInstance.instanceId}/notes/${noteId}`), payload)
			.then( result => {
				if (result) {
					notes.splice(index, 1, newValue);
					setNotes([...notes]);
				}
			})
			.catch(error => {
				const message = IS_DEV ? error.message : dictionary.error;
				notifyError(message, error);
			})
		onNoteClose();
	};

	const handleConfirmDelete = () => {
		const noteId = notes[noteModal.noteIndex].noteId;
		remove(spUrl(`${formInstance.endpoint}/${formInstance.instanceId}/notes/${noteId}`))
			.then(result =>{
				if (result){
					notes.splice(noteModal.noteIndex, 1);
					setNotes([ ...notes ]);
				}
			})
			.catch(error => {
				const message = IS_DEV ? error.message : dictionary.error;
				notifyError(message, error);
			})
		handleIsConfirmDeleteOpen();
	};

	const handleIsConfirmDeleteOpen = index => {
		setNoteModal({ ...noteModal, noteIndex: isConfirmDeleteOpen ? 0 : index });
		setIsConfirmDeleteOpen(!isConfirmDeleteOpen);
	};

	const handleSort = () => {
		setNotes([ ...notes.reverse() ]);
		setSortOrder(sortOrder === "ascending" ? "descending" : "ascending");
	};

	const handleFindUsers = useCallback(() => {
		const userIdsToFetchHash = {};
		const names = [ ...userNames ];
		const findExistingNameById = id => names.find(({ nameId }) => id === nameId);
		notes.forEach(({ updatedBy, createdBy }) => {
			if (!findExistingNameById(updatedBy)) {
				userIdsToFetchHash[updatedBy] = true;
			}
			if (!findExistingNameById(createdBy)) {
				userIdsToFetchHash[createdBy] = true;
			}
		});
		const userIds = Object.keys(userIdsToFetchHash);
		const hasIdsToFetch = isNonEmptyArray(userIds);
		if (hasIdsToFetch) {
			Promise.all(userIds.map(userId => {
				return findUsers({
					pageLimit: 100,
					pageOffset: 0,
					searchTerms: [
						{ key: "personId", val: userId },
					],
					includeInactive: true,
				})
					.then(response => {
						const person = response.find(person => person.personId === userId);

						return names.push({
							name: isNonEmptyString(person?.fullName)
								? person.fullName
								: dictionary.unknownUser || "Unknown User",
							nameId: userId,
						});
					})
					.catch(error => {
						notifyError("Something went wrong. Please try again.", error);
					});
			}))
				.finally(() => {
					setUserNames(names);

					setUpdateNotes(true);
				});
		}else{
			setUpdateNotes(true);
		}
	}, [ notes, dictionary.unknownUser, userNames ]);

	useEffect(() => {
		if(reloadNotes && formInstance.instanceId) {
			request(spUrl(`${formInstance.endpoint}/${formInstance.instanceId}/notes?page=${pageNumber}&size=${itemsPerPage}`))
				.then(data => {
					const notes = isProject ? data.content : data; //This will be removed when we do the notes roll up
					setTotalNotes(isProject ? data.totalElements : data.length); //This will be removed when we do the notes roll up
					setNotes(Pager.pageSlice(notes.map(note => {
						return {
							...note,
							updatedBy: note.updatedBy || note.createdBy,
							updatedOn: note.updatedOn || note.createdOn,
						};
					}), itemsPerPage, isProject ? 0 : pageNumber));

					setHasNotes(isNonEmptyArray(notes));

					if (isNonEmptyArray(notes)) {
						setUpdateNames(true);
						setIsLoadingNotes(true);
					} else {
						setUpdateNames(false);
						setIsLoadingNotes(false);
					}
					setReloadNotes(false);
				});
		}
	}, [ formInstance.endpoint, formInstance.instanceId, reloadNotes ]);

	useEffect(() => {
		if (hasNotes && updateNames) {
			setUpdateNames(false);
			handleFindUsers();
		}

		if (updateNotes) {
			const updatedNotes = notes.map(note => {
				return {
					...note,
					updatedBy: userNames?.find(name => name.nameId === note.updatedBy)?.name,
					createdByName: userNames?.find(name => name.nameId === note.createdBy)?.name,
				};
			});

			setNotes(updatedNotes);
			setUpdateNotes(false);
			setIsLoadingNotes(false);
		}
	}, [
		notes,
		dictionary.unknownUser,
		handleFindUsers,
		hasNotes,
		updateNotes,
		updateNames,
		userNames,
	]);

	const loadNextPage = nextPageNumber => {
		nextPageNumber--;
		setPageNumber(nextPageNumber);
		setReloadNotes(true);
	};

	const updatePageSize = itemsPerPage => {
		const numberOfPages = Math.ceil(notes / itemsPerPage);
		setItemsPerPage(itemsPerPage);
		setNumberOfPages(numberOfPages);
		setReloadNotes(true);
	};

	return (
		<>
			{canAddNotes ?
				<VerticalSpacing>
					<div className={styles.addNote}>
						<span className={styles.label}>{dictionary.addNote || "Add a note"}</span>
						<div className={styles.editNote} ref={richTextRef}>
							<RichText
								maxLength={NOTES_MAX_LENGTH.toString()}
								onChange={({ value }) => onNewNoteChange(value)}
								placeholder={dictionary.notePlaceholder || "Enter your note text here..."}
								value={newNoteMessage}
								formats={richTextFormats}
							/>
							{hasInvalidData()
								? <span className={styles.validationMessage}>
									{dictionary.lengthError}
								</span>  : ""}
						</div>
						<Button disabled={!hasNewNoteMessage || hasInvalidData()} onClick={handleOnSave}>
							{dictionary.actions.save || "Save"}
						</Button>
					</div>
				</VerticalSpacing>
				: ""
			}

			{noteModal.isVisible &&
				<NoteDisplayModal
					title={
						noteModal.canEdit
							? dictionary.noteModal.editTitle || "Edit Note"
							: dictionary.noteModal.viewTitle || "Note Details"
					}
					note={notes[noteModal.noteIndex]}
					noteModal={noteModal}
					richTextFormats={richTextFormats}
					onClose={onNoteClose}
					update={handleOnUpdate}
				/>
			}
			{isConfirmDeleteOpen &&
				<ConfirmationModal
					icon="file-alt"
					visible={isConfirmDeleteOpen}
					title={dictionary.confirmationModal.title || "Delete Note"}
					message={
						<FormattedRichText
							value={decodeString(notes[noteModal.noteIndex].message)}
							maxLength="70"
							singleLine
						/>
					}
					onConfirm={handleConfirmDelete}
					onCancel={handleIsConfirmDeleteOpen}
				/>
			}

			{!isLoadingNotes &&
			<div>
				<VerticalSpacing>
					<Table sortable captionText="Form Notes">
						<Table.Header>
							<Table.Row>
								<Table.HeaderCell>{dictionary.columnHeaders?.note || "Note"}</Table.HeaderCell>
								<Table.HeaderCell
									aria-label={
										`Sorted ${sortOrder} ${dictionary.columnHeaders?.sortByLastUpdated || "Sort by Last Updated Date"} in ${sortOrder === "ascending" ? "descending" : "ascending"} order`
									}
									sorted={sortOrder}
									onClick={handleSort}
									role="button"
									tabindex="0"
									style={{ cursor: "pointer" }}
								>
									{dictionary.columnHeaders?.updatedBy || "Last Updated By"}
								</Table.HeaderCell>
								<Table.HeaderCell>{dictionary.columnHeaders?.date || "Date"}</Table.HeaderCell>
								<Table.HeaderCell/>
							</Table.Row>
						</Table.Header>

						<Table.Body>
							{hasNotes
								? (notes.map((note, index) => (
									<Table.Row key={note.noteId}>
										<Table.Cell className={styles.messageCell}>
											<button
												type="button"
												onClick={() => onNoteOpen(false, index)}
												className={styles.link}
												aria-label="Open note details"
												aria-haspopup="dialog"
											>
												<FormattedRichText value={normalizeNoteMessage(note.message)} maxLength="70" singleLine/>
											</button>
										</Table.Cell>

										<Table.Cell>{note.updatedBy}</Table.Cell>

										<Table.Cell>
											<FormattedDatetime date={note.updatedOn} />
										</Table.Cell>

										<Table.Cell>
											{canEditNotes || note.createdBy === personId ?
												<div className={styles.rowActions}>
													<CovertButton
														className={styles.iconButton}
														onClick={() => onNoteOpen(true, index)}
														aria-label="Edit note"
													>
														<Icon name={"pencil"} />
													</CovertButton>
													<CovertButton
														className={styles.iconButton}
														onClick={() => handleIsConfirmDeleteOpen(index)}
														aria-label="Delete note"
													>
														<Icon name={"trash"} />
													</CovertButton>
												</div>
												: ""
											}
										</Table.Cell>
									</Table.Row>
								)))
								: ""
							}
						</Table.Body>
					</Table>
				</VerticalSpacing>
				<div className={styles.pagingControls}>
					<PagingControls
						items={notes}
						onPageChange={loadNextPage}
						totalItems={totalNotes}
						pageLimit={itemsPerPage}
						pageOffset={pageNumber}
						totalPages={numberOfPages}
						onPageSizeChange={updatePageSize}
					/>
				</div>
			</div>
			}
		</>
	);
};

export default FormNotesView;
